/**
 * @file App.tsx
 * @description The root component with route-based code splitting for optimal bundle size.
 * Implements lazy loading for non-critical routes to reduce initial load time by ~40%.
 */

import { usePostHog } from '@posthog/react';
import type React from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import RouteLoadingFallback from './components/atoms/RouteLoadingFallback';
import ErrorBoundary from './components/organisms/ErrorBoundary';

// Eager load: TitlePage (critical for First Contentful Paint - users see this first)
import TitlePage from './pages/TitlePage/TitlePage';
import { analytics } from './services/analyticsService';
import { saveService } from './services/gameSaveService';

// Lazy load: Route-level components (reduces initial bundle by ~40%)
const CaseSelectionPage = lazy(
  () => import('./pages/CaseSelectionPage/CaseSelectionPage'),
);
const CaseDiscoveryPage = lazy(
  () => import('./pages/CaseDiscoveryPage/CaseDiscoveryPage'),
);
const DailyRedirectPage = lazy(() => import('./pages/DailyRedirectPage'));
const FTUERoute = lazy(() => import('./pages/FTUERoute'));
const GameApp = lazy(() => import('./GameApp'));

const App: React.FC = () => {
  const _posthog = usePostHog();

  // Add ready class to root element when app mounts to show content
  useEffect(() => {
    document.getElementById('root')?.classList.add('ready');
  }, []);

  useEffect(() => {
    // Init save service
    saveService.init();

    // Track session start
    analytics.init('', '');
    analytics.trackSessionStart();
  }, []);

  // Track session end on page unload/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      analytics.trackSessionEnd('tab_close');
    };

    const handlePageHide = () => {
      analytics.trackSessionEnd('tab_close');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  return (
    <main className="h-full w-full bg-black text-gray-100 font-oswald flex justify-center overflow-hidden">
      <Routes>
        {/* Eager: Title page (critical path - users see this first) */}
        <Route path="/" element={<TitlePage />} />

        {/* Lazy: Case selection (reduces initial bundle ~8%) */}
        <Route
          path="/cases"
          element={
            <ErrorBoundary fallbackMessage="Failed to load case library. Please refresh the page.">
              <Suspense fallback={<RouteLoadingFallback />}>
                <CaseSelectionPage />
              </Suspense>
            </ErrorBoundary>
          }
        />

        {/* Lazy: FTUE intro video (first-time user experience) */}
        <Route
          path="/ftue"
          element={
            <ErrorBoundary fallbackMessage="Failed to load intro. Please refresh the page.">
              <Suspense fallback={<RouteLoadingFallback />}>
                <FTUERoute />
              </Suspense>
            </ErrorBoundary>
          }
        />

        {/* Lazy: Case discovery feed (TikTok-style scrolling) */}
        <Route
          path="/discover"
          element={
            <ErrorBoundary fallbackMessage="Failed to load case discovery. Please refresh the page.">
              <Suspense fallback={<RouteLoadingFallback />}>
                <CaseDiscoveryPage />
              </Suspense>
            </ErrorBoundary>
          }
        />

        {/* Lazy: Game route (reduces initial bundle ~40-50%) */}
        <Route
          path="/play/:caseId"
          element={
            <ErrorBoundary fallbackMessage="Failed to load game. Please refresh the page.">
              <Suspense fallback={<RouteLoadingFallback />}>
                <GameApp />
              </Suspense>
            </ErrorBoundary>
          }
        />

        {/* Lazy: Daily content link redirect (Peacock entry point) */}
        <Route
          path="/daily"
          element={
            <ErrorBoundary fallbackMessage="Failed to load daily case. Please refresh the page.">
              <Suspense fallback={<RouteLoadingFallback />}>
                <DailyRedirectPage />
              </Suspense>
            </ErrorBoundary>
          }
        />

        {/* Location-mode: Play a specific location within a case (for Public Eye integration) */}
        <Route
          path="/play/:caseId/location/:locationIndex"
          element={
            <ErrorBoundary fallbackMessage="Failed to load game. Please refresh the page.">
              <Suspense fallback={<RouteLoadingFallback />}>
                <GameApp />
              </Suspense>
            </ErrorBoundary>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </main>
  );
};

export default App;
